import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { ACTION_CATEGORY, ACTION_NAME, ACTION_OBJECT } from "../../constants";
import { useUrlParams } from "../../hooks";
import { ActionService } from "../../services";
import { currentExperienceSelector, currentMediaShareSelector } from "../../store/selectors";

export function useShareLinkTracking() {
  const [hasTrackedShareLink, setHasTrackedShareLink] = useState(false);

  const { propertyId, experienceId, userId } = useUrlParams();

  const { experience } = useSelector(currentExperienceSelector);
  const { sharedById, sharedContactId } = experience;

  const { mediaShare } = useSelector(currentMediaShareSelector);

  // Full Collection does not create an experience, so, we
  // need to provide the current location as shareUrl (SH-7064)
  let shareUrl = experience?.shareUrl || location.href;

  const getIpAddress = () =>
    fetch("https://www.cloudflare.com/cdn-cgi/trace")
      .then((res) => res.text())
      .then((data) => /\d{1,3}.\d{1,3}.\d{1,3}.\d{1,3}/.exec(data)[0]);

  useEffect(() => {
    (async () => {
      const shareId = new URLSearchParams(location.search).get("shareId");
      // because we don't have an experience when sharing a full collection,
      // we need to add processed as false to notify the user
      const isAllAssetShareRoute = location.pathname.includes("all-assets-share");

      // If is not an all asset share route and we don't have an experience id, we were
      // creating an action without a defined recipient, and then marking the action as
      // processed. This was preventing the notification from being sent to the user.
      if (!isAllAssetShareRoute && !experience.id) {
        return;
      }

      // if we are in an all asset share route and we don't have a media share id, it has
      // probably not finished loading yet, so we don't want to create an action
      if (isAllAssetShareRoute && !mediaShare?.id) {
        return;
      }

      if (shareId && !hasTrackedShareLink && shareUrl) {
        if (userId && userId === sharedById) {
          return;
        }

        try {
          const ipAddress = await getIpAddress();
          const values = {
            name: ACTION_NAME.VIEWED,
            category: ACTION_CATEGORY.ENGAGEMENT,
            object: isAllAssetShareRoute ? ACTION_OBJECT.FULL_COLLECTION : ACTION_OBJECT.EXPERIENCE,
            objectName: experience.title,
            shareUrl,
            experienceId,
            propertyId,
            shareId: +shareId,
            to: sharedById ? [sharedById] : [],
            ipAddress,
            // processed if we don't have sharedById ( otherwise we have no one to notify)
            // or if is all asset share, meaning that we won't create an experience
            processed: !isAllAssetShareRoute && !sharedById,
          };

          if (userId) {
            values.userId = userId;
          }

          if (sharedContactId) {
            values.contactId = sharedContactId;
          }

          if (isAllAssetShareRoute && !values.contactId && mediaShare.contactId) {
            values.contactId = mediaShare.contactId;
          }

          await ActionService.addAction(values);
        } catch (error) {
          console.error(error);
        } finally {
          setHasTrackedShareLink(true);
        }
      }
    })();
  }, [hasTrackedShareLink, sharedById, mediaShare?.id]);

  return { hasTrackedShareLink, setHasTrackedShareLink };
}
